import { useEffect, useState } from "react";
import { PaginateObj } from "../../types/Paginate/PaginateObjt";
import { User } from "../../types/Usuario/User";
import { Box, Checkbox, Container, FormControlLabel, Grid, Pagination, Paper, styled, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { themePalette } from "../../config/theme.config";
import AccountService from "../../services/AccountService";
import { EditRoleDTO } from "../../types/Usuario/EditRoleDTO";
import SweetAlertUtil from "../../utils/SweetAlert";

interface Props {
    page: PaginateObj<User>;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

export const UserTable: React.FC<Props> = ({ page }) => {
    const [iPage, setIpage] = useState<PaginateObj<User>>(page);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [creadores, setCreadores] = useState<Array<string>>([]);
    const [revisores, setRevisores] = useState<Array<string>>([]);
    const [administradores, setAdministradores] = useState<Array<string>>([]);
    // Function
    const checkRolCreador = (id: string, rol: string) => {
        const role: EditRoleDTO = {
            UserId: id,
            RoleName: rol
        }
        if (SoyCreador(id)) {
            AccountService.RemoverRol(role)
                .then((value) => {
                    if (value.status === 204) {
                        SweetAlertUtil.alertaExito('Exito', 'Usuario actualizdo')
                    } else {
                        SweetAlertUtil.alertaAdvertencia('Alerta', value.message);
                    }
                })
                .catch((error) => {
                    //console.error(error)
                    SweetAlertUtil.alertaAdvertencia('Alerta', 'Error al remover rol');
                }).finally(
                    () => fetchCreadores()
                )
        } else {
            AccountService.AsignarRol(role)
                .then((value) => {
                    if (value.status === 204) {
                        SweetAlertUtil.alertaExito('Exito', 'Usuario actualizdo')
                    } else {
                        SweetAlertUtil.alertaAdvertencia('Alerta', value.message);
                    }
                })
                .catch((error) => {
                    //console.error(error)
                    SweetAlertUtil.alertaAdvertencia('Alerta', 'Error al asignar rol');
                }).finally(
                    () => fetchCreadores()
                )
        }
    }
    const checkRolRevisor = (id: string, rol: string) => {
        const role: EditRoleDTO = {
            UserId: id,
            RoleName: rol
        }
        if (SoyRevisor(id)) {
            AccountService.RemoverRol(role)
                .then((value) => {
                    if (value.status === 204) {
                        SweetAlertUtil.alertaExito('Exito', 'Usuario actualizdo')
                    } else {
                        SweetAlertUtil.alertaAdvertencia('Alerta', value.message);
                    }
                })
                .catch((error) => {
                    console.error(error)
                    SweetAlertUtil.alertaAdvertencia('Alerta', 'Error al remover rol');
                }).finally(
                    () => fetchRevisores()
                )
        } else {
            AccountService.AsignarRol(role)
                .then((value) => {
                    if (value.status === 204) {
                        SweetAlertUtil.alertaExito('Exito', 'Usuario actualizdo')
                    } else {
                        SweetAlertUtil.alertaAdvertencia('Alerta', value.message);
                    }
                })
                .catch((error) => {
                    console.error(error)
                    SweetAlertUtil.alertaAdvertencia('Alerta', 'Error al asignar rol');
                }).finally(
                    () => fetchRevisores()
                )
        }
    }
    const checkRolAdmin = (id: string, rol: string) => {
        const role: EditRoleDTO = {
            UserId: id,
            RoleName: rol
        }
        if (SoyAdmin(id)) {
            AccountService.RemoverRol(role)
                .then((value) => {
                    if (value.status === 204) {
                        SweetAlertUtil.alertaExito('Exito', 'Usuario actualizdo')
                    } else {
                        SweetAlertUtil.alertaAdvertencia('Alerta', value.message);
                    }
                })
                .catch((error) => {
                    console.error(error)
                    SweetAlertUtil.alertaAdvertencia('Alerta', 'Error al remover rol');
                }).finally(
                    () => fetchAdmins()
                )
        } else {
            AccountService.AsignarRol(role)
                .then((value) => {
                    if (value.status === 204) {
                        SweetAlertUtil.alertaExito('Exito', 'Usuario actualizdo')
                    } else {
                        SweetAlertUtil.alertaAdvertencia('Alerta', value.message);
                    }
                })
                .catch((error) => {
                    console.error(error)
                    SweetAlertUtil.alertaAdvertencia('Alerta', 'Error al asignar rol');
                }).finally(
                    () => fetchAdmins()
                )
        }
    }
    function SoyCreador(id: string): boolean {
        return creadores.includes(id);
    }
    function SoyRevisor(id: string): boolean {
        return revisores.includes(id);
    }
    function SoyAdmin(id: string): boolean {
        return administradores.includes(id);
    }
    const fetchCreadores = async () => {
        setLoading(true)
        try {
            const response = await AccountService.GetIdUserByRol('Creador');
            const data = response;
            //console.log(data);
            if (response) {
                setCreadores(data);
            }
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }
    const fetchRevisores = async () => {
        setLoading(true)
        try {
            const response = await AccountService.GetIdUserByRol('Revisor');
            const data = response;
            // console.log(data);
            if (response) {
                setRevisores(data);
            }
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }
    const fetchAdmins = async () => {
        setLoading(true)
        try {
            const response = await AccountService.GetIdUserByRol('Administrador');
            const data = response;
            // console.log(data);
            if (response) {
                setAdministradores(data);
            }
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }
    const fetchUsuarios = async () => {
        setLoading(true)
        try {
            if (iPage.totalPages > 1) {
                const response = await AccountService.GetPageUserByNumber(iPage.firstPage, currentPage)
                const data = response;
                // console.log(data);
                if (response.succeeded) {
                    setIpage(data);
                }
            }
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }
    const handleChangePage = (event: unknown, newPage: number) => {
        setCurrentPage(newPage); // Cambia la página
    };
    //Effect
    useEffect(() => {
        fetchCreadores();
        fetchRevisores();
        fetchAdmins();
    }, [])
    useEffect(() => {
        fetchUsuarios();
    }, [currentPage]);
    return (
        <Container maxWidth="lg" style={{ marginTop: 10, marginLeft: 20, padding: '20px', textAlign: 'center' }}>
            {iPage.data.length === 0
                ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center', // Alinea el texto
                            backgroundColor: themePalette.BGWHITE, // Fondo claro
                        }}
                    >
                        <Typography variant="h4" color="primary">
                            Sin resultados
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                            {iPage.totalPages > 1 ? (
                                <Pagination
                                    count={iPage.totalPages} // Total de páginas
                                    page={currentPage} // Página actual
                                    onChange={handleChangePage} // Cambiar página
                                    color="primary"
                                    sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}
                                />
                            ) : (
                                <br />
                            )}
                        </Box>
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 700 }} aria-label="customized table">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>#</StyledTableCell>
                                        <StyledTableCell>Nombre</StyledTableCell>
                                        <StyledTableCell >Usuario</StyledTableCell>
                                        <StyledTableCell >Creador</StyledTableCell>
                                        <StyledTableCell >Revisor</StyledTableCell>
                                        <StyledTableCell >Administrador</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {iPage.data.map((row, index) => (
                                        <StyledTableRow key={row.userName}>
                                            <StyledTableCell component="th" scope="row">
                                                {index + (currentPage - 1) * 8 + 1}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {row.txNombres + " " + row.txApellidos}
                                            </StyledTableCell>
                                            <StyledTableCell>{row.userName}</StyledTableCell>
                                            <StyledTableCell>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={SoyCreador(row.id)}
                                                            onClick={() => checkRolCreador(row.id, 'Creador')}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={creadores.includes(row.id) ? "Asignado" : "Removido"}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={SoyRevisor(row.id)}
                                                            onClick={() => checkRolRevisor(row.id, 'Revisor')}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={revisores.includes(row.id) ? "Asignado" : "Removido"}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={SoyAdmin(row.id)}
                                                            onClick={() => checkRolAdmin(row.id, 'Administrador')}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={administradores.includes(row.id) ? "Asignado" : "Removido"}
                                                />
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                            {iPage.totalPages > 1 && (
                                <Pagination
                                    count={iPage.totalPages} // Total de páginas
                                    page={currentPage} // Página actual
                                    onChange={handleChangePage} // Cambiar página
                                    color="primary"
                                    sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}
                                />
                            )}
                        </Box>
                    </Grid>
                )}
        </Container>
    )
}